import { Injectable } from '@angular/core';

import { select, Store } from '@ngrx/store';
import { Group, GroupCreationData, SelectedStudent, TeacherGroup } from '@tekman/onmat/models';
import { filter, map } from 'rxjs/operators';
import * as GroupsActions from './groups.actions';
import * as GroupsSelectors from './groups.selectors';

@Injectable()
export class GroupsFacade {
  loaded$ = this._store.pipe(select(GroupsSelectors.getGroupsLoaded));
  saveGroupLoaded$ = this._store.pipe(
    select(GroupsSelectors.getSaveGroupLoaded),
    filter(loaded => !!loaded)
  );
  groups$ = this._store.pipe(
    select(GroupsSelectors.getAllGroups),
    map(data => {
      data.sort((a, b) => {
        const isEGB_A = a.courseName.includes('EGB');
        const isEGB_B = b.courseName.includes('EGB');

        if (isEGB_A && !isEGB_B) return -1;
        if (!isEGB_A && isEGB_B) return 1;

        if (isEGB_A && isEGB_B) {
          const numA = parseInt(a.courseName);
          const numB = parseInt(b.courseName);
          return numA - numB;
        }

        return a.courseName.localeCompare(b.courseName);
      });
      return data;
    })
  );
  students$ = this._store.pipe(select(GroupsSelectors.getStudentsGroup));
  editableStudents$ = this._store.pipe(select(GroupsSelectors.getEditableStudentsGroup));
  allTeachers$ = this._store.pipe(
    select(GroupsSelectors.getAllTeachers),
    filter(teachers => !!teachers)
  );
  editableGroup$ = this._store.pipe(
    select(GroupsSelectors.getEditableGroup),
    filter(group => !!group)
  );
  selectedStudent$ = this._store.pipe(select(GroupsSelectors.getSelectedStudent));
  failedStudents$ = this._store.pipe(
    select(GroupsSelectors.getFailedStudents),
    filter(student => !!student)
  );
  successStudents$ = this._store.pipe(
    select(GroupsSelectors.getSuccessStudents),
    filter(student => !!student)
  );
  failedTeachers$ = this._store.pipe(
    select(GroupsSelectors.getFailedTeachers),
    filter(teachers => !!teachers)
  );
  selectedGroup$ = this._store.pipe(select(GroupsSelectors.getSelectedGroup));
  error$ = this._store.pipe(select(GroupsSelectors.getGroupsError));
  alreadyImportedGroup$ = this._store.pipe(
    select(GroupsSelectors.getAlreadyImportedGroup),
    filter(alreadyImportedGroup => !!alreadyImportedGroup)
  );
  teams$ = this._store.pipe(select(GroupsSelectors.getTeams));
  teachers$ = this._store.pipe(select(GroupsSelectors.getCenterTeachers));

  constructor(private _store: Store) {}

  getSelectedGroup() {
    return this._store
      .pipe(select(GroupsSelectors.getSelectedGroup))
      .pipe(filter(group => !!group && !!group.itinerary && !!group.progress));
  }

  loadGroupsByYear(academicYearId: string) {
    this._store.dispatch(GroupsActions.loadGroupsByAcademicYear({ academicYearId }));
  }

  loadEvaluateStudent(selectedStudent: SelectedStudent) {
    this._store.dispatch(GroupsActions.selectStudent({ selectedStudent }));
  }

  selectGroup(selectedGroup: Group): void {
    this._store.dispatch(GroupsActions.selectGroup({ selectedGroup }));
  }

  getEditableGroup(id: string) {
    this._store.dispatch(GroupsActions.loadEditableGroup({ id }));
  }

  getAllTeachers() {
    this._store.dispatch(GroupsActions.loadEditableTeachers());
  }

  getCenterTeachers(academicYearId: string): void {
    this._store.dispatch(GroupsActions.loadTeachers({ academicYearId }));
  }

  deleteTeacher(teacherId: string): void {
    this._store.dispatch(GroupsActions.deleteTeacher({ teacherId }));
  }

  saveGroup(groupCreation: GroupCreationData) {
    this._store.dispatch(GroupsActions.saveGroup({ groupCreation }));
  }

  resetAlreadyImported() {
    this._store.dispatch(GroupsActions.resetSaveGroupAlreadyImported());
  }

  updateStudentPassword(id: string, password: string) {
    this._store.dispatch(GroupsActions.updatePassword({ studentId: id, password: password }));
  }

  updateStudentInfo(id: string, name: string, lastName: string) {
    this._store.dispatch(GroupsActions.updateStudentInfo({ studentId: id, name, lastName }));
  }

  deleteStudent(groupId: string, studentId: string) {
    this._store.dispatch(GroupsActions.deleteStudent({ groupId, studentId }));
  }

  inviteStudents(groupId: string, userNames: string[]) {
    this._store.dispatch(GroupsActions.inviteStudents({ groupId, userNames }));
  }

  removeFailedStudentsImported() {
    this._store.dispatch(GroupsActions.removeFailedStudentsImported());
  }

  updateProgress(stepId: string, groupId: string): void {
    this._store.dispatch(GroupsActions.updateProgress({ stepId, groupId }));
  }

  saveTeachers(teachers: TeacherGroup[]): void {
    this._store.dispatch(GroupsActions.saveTeachers({ teachers }));
  }

  addStepToItinerary(itineraryId: string, stepId: string, groupId: string) {
    this._store.dispatch(GroupsActions.addStepToItinerary({ itineraryId, stepId, groupId }));
  }

  deleteStepInItinerary(itineraryId: string, stepId: string, groupId: string) {
    this._store.dispatch(GroupsActions.deleteStepInItinerary({ itineraryId, stepId, groupId }));
  }

  updateStepCompleted(stepId: string, groupId: string, isCompleted: boolean) {
    this._store.dispatch(GroupsActions.updateStepCompleted({ stepId, groupId, isCompleted }));
  }

  resetAlerts() {
    this._store.dispatch(GroupsActions.resetAlerts());
  }
}
